import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";

const FRAME_SIZE = 28;
const VAE_WS_URL = "wss://api.materialtalk.co/ws/vae"; 

// --- SVG Icons ---
const Ss=()=><svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M22.539 8.242H1.46V5.406h21.08v2.836zM1.46 10.812V24L12 18.11 22.54 24V10.812H1.46zM22.54 0H1.46v2.836h21.08V0z"/></svg>;
const InstagramIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="20" rx="5" />
    <circle cx="12" cy="12" r="5" />
    <circle cx="17.5" cy="6.5" r="1.2" fill="currentColor" stroke="none" />
  </svg>
);
const LinkedInIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5v-9h3zM6.5 8.25A1.75 1.75 0 118.3 6.5a1.78 1.78 0 01-1.8 1.75zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.74 1.74 0 0013 14.19V19h-3v-9h2.9v1.3a3.11 3.11 0 012.7-1.4c1.55 0 3.36.86 3.36 3.66z" />
  </svg>
);
const TikTokIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 0010.86 4.43v-7.15a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-.81.07z" />
  </svg>
);
const YouTubeIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M23.5 6.19a3.02 3.02 0 00-2.12-2.14C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.38.55A3.02 3.02 0 00.5 6.19 31.6 31.6 0 000 12a31.6 31.6 0 00.5 5.81 3.02 3.02 0 002.12 2.14c1.88.55 9.38.55 9.38.55s7.5 0 9.38-.55a3.02 3.02 0 002.12-2.14A31.6 31.6 0 0024 12a31.6 31.6 0 00-.5-5.81zM9.75 15.02V8.98L15.5 12z" />
  </svg>
);
const SpotifyIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.52 17.34c-.24.36-.66.48-1.02.24-2.82-1.74-6.36-2.1-10.56-1.14-.42.12-.78-.18-.9-.54-.12-.42.18-.78.54-.9 4.56-1.02 8.52-.6 11.7 1.32.42.18.48.66.24 1.02zm1.44-3.3c-.3.42-.84.6-1.26.3-3.24-1.98-8.16-2.58-11.94-1.38-.48.12-.96-.12-1.08-.6s.12-.96.6-1.08c4.38-1.32 9.78-.66 13.5 1.62.36.18.48.78.18 1.14zm.12-3.36C15.24 8.4 8.88 8.16 5.16 9.3c-.6.18-1.14-.18-1.32-.72-.18-.6.18-1.14.72-1.32 4.26-1.26 11.28-1.02 15.72 1.56.54.3.72 1.02.42 1.56-.3.42-.96.6-1.5.3z" />
  </svg>
);
const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <circle cx="11" cy="11" r="7" />
    <line x1="16.5" y1="16.5" x2="21" y2="21" />
  </svg>
);

// --- VAE Canvas Component (dual-mode: live server / demo fallback) --------
function VAEAnimation() {
  const canvasRef = useRef(null);
  const wsRef = useRef(null);
  const latestFrameRef = useRef(null);       // latest frame from server (Uint8Array)
  const demoFramesRef = useRef(null);         // fallback demo frames
  const demoIndexRef = useRef(0);
  const speedRef = useRef(0.3);
  const lastMouseRef = useRef({ x: 0, y: 0, t: Date.now() });
  const accumulatorRef = useRef(0);
  const modeRef = useRef("connecting");       // "connecting" | "live" | "demo"
  const [mode, setMode] = useState("connecting");

  // --- Attempt WebSocket connection to VAE server ---
  useEffect(() => {
    let ws;
    let reconnectTimer;
    let closed = false;

    const connect = () => {
      try {
        ws = new WebSocket(VAE_WS_URL);
        ws.binaryType = "arraybuffer";

        ws.onopen = () => {
          console.log("[VAE] Connected to live server");
          wsRef.current = ws;
          modeRef.current = "live";
          setMode("live");
        };

        ws.onmessage = (event) => {
          if (event.data instanceof ArrayBuffer) {
            latestFrameRef.current = new Uint8Array(event.data);
          }
        };

        ws.onclose = () => {
          if (closed) return;
          wsRef.current = null;
          // Retry connection after 3 seconds
          setTimeout(() => {
            if (!closed) connect();
          }, 3000);
        };

        ws.onerror = () => {
          ws.close();
        };
      } catch {
        fallbackToDemo();
      }

      // If not connected within 2s, fall back
      reconnectTimer = setTimeout(() => {
        if (modeRef.current === "connecting") fallbackToDemo();
      }, 10000);
    };

    const fallbackToDemo = () => {
  if (modeRef.current === "hidden") return;
  modeRef.current = "hidden";
  setMode("hidden");
};

    connect();

    return () => {
      closed = true;
      clearTimeout(reconnectTimer);
      if (ws && ws.readyState <= 1) ws.close();
    };
  }, []);

  // --- Mouse speed tracking → controls animation + tells server ---
  useEffect(() => {
    let sendTimer;
    const handleMouseMove = (e) => {
      const now = Date.now();
      const last = lastMouseRef.current;
      const dt = Math.max(1, now - last.t);
      const dx = e.clientX - last.x;
      const dy = e.clientY - last.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const velocity = dist / dt;
      speedRef.current = 0.15 + Math.min(velocity * 2.5, 3.0);
      lastMouseRef.current = { x: e.clientX, y: e.clientY, t: now };

      // Throttled: send speed to server (live mode only)
      if (!sendTimer && wsRef.current && wsRef.current.readyState === 1) {
        sendTimer = setTimeout(() => {
          try {
            wsRef.current.send(JSON.stringify({ speed: speedRef.current }));
          } catch { /* ignore */ }
          sendTimer = null;
        }, 100); // max 10 updates/sec to server
      }
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      clearTimeout(sendTimer);
    };
  }, []);

  // --- Render loop ---
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const DISPLAY = 120;
    const offscreen = new OffscreenCanvas(FRAME_SIZE, FRAME_SIZE);
    const octx = offscreen.getContext("2d");
    const imgData = octx.createImageData(FRAME_SIZE, FRAME_SIZE);
    let animId;

    const renderFrame = (pixelData, isUint8) => {
      for (let i = 0; i < FRAME_SIZE * FRAME_SIZE; i++) {
        const v = isUint8 ? pixelData[i] / 255 : pixelData[i];
        imgData.data[i * 4]     = Math.floor(v * 215 + (1 - v) * 10);
        imgData.data[i * 4 + 1] = Math.floor(v * 185 + (1 - v) * 10);
        imgData.data[i * 4 + 2] = Math.floor(v * 130 + (1 - v) * 10);
        imgData.data[i * 4 + 3] = 255;
      }
      octx.putImageData(imgData, 0, 0);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.clearRect(0, 0, DISPLAY, DISPLAY);
      ctx.drawImage(offscreen, 0, 0, DISPLAY, DISPLAY);
    };

    const draw = () => {
      speedRef.current = Math.max(0.15, speedRef.current * 0.97);

      if (modeRef.current === "live" && latestFrameRef.current) {
        // Live mode: render the latest frame from the server
        renderFrame(latestFrameRef.current, true);
      } else if (modeRef.current === "demo" && demoFramesRef.current) {
        // Demo mode: advance through local cache
        accumulatorRef.current += speedRef.current;
        if (accumulatorRef.current >= 1) {
          const steps = Math.floor(accumulatorRef.current);
          accumulatorRef.current -= steps;
          demoIndexRef.current =
            (demoIndexRef.current + steps) % demoFramesRef.current.length;
        }
        renderFrame(demoFramesRef.current[demoIndexRef.current], false);
      }

      animId = requestAnimationFrame(draw);
    };
    animId = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animId);
  }, []);

  const statusLabel = mode === "live" ? "live from vae" : mode === "demo" ? "move mouse to animate" : "connecting...";

  if (mode === "hidden") return null;

  return (
    <div style={{ position: "relative" }}>
      <canvas
        ref={canvasRef}
        width={120}
        height={120}
        style={{ width: 120, height: 120, borderRadius: 4 }}
      />
      {/* <div style={{
        position: "absolute",
        bottom: -20,
        left: 0, right: 0,
        textAlign: "center",
        fontSize: 9,
        letterSpacing: "0.15em",
        color: mode === "live" ? "#6ec97a" : "#f5f0eb",
        opacity: 0.7,
        fontFamily: "'DM Sans', sans-serif",
        textTransform: "uppercase",
        transition: "color 0.5s ease",
      }}>
        {statusLabel}
      </div> */}
    </div>
  );
}

// --- Main App ---
const NAV_ITEMS = ["Home", "About", "Fashboard", "Articles", "Podcast",  "People", "Contact"];

const HERO_ARTICLE = {
  category: "Op-Ed",
  title: "Fashion Tech is sexy but numbers are not. Let’s talk about it…",
  subtitle: "Everyone’s calling fashion tech “the next big thing.” AI stylists, virtual try-on, mega valuations, founders posting “we just closed our round” with the champagne emoji. The vibe online is: the money is flowing, get in now.",
  author: "Julia Karpuk",
  date: "July 16, 2026",
  image: "/images/articles/karpuk_26/karpuk_header.jpeg",
  href: "/articles/karpuk_26"
};

const SIDE_ARTICLES = [
  { category: "Op-Ed", title: "The Real Cost of Fashion's Trend Forecasting Monopoly", color: "#221a1f", image: "/images/articles/brushcode/brushcode5.png", href: "/articles/brushcode" },
  { category: "Op-Ed", title: "Will AI Kill the Warehouse Sale?", color: "#2a1f15", image: "/images/articles/Couth_Studios/nyc_warehouse.JPG", href: "/articles/couthstudios" },
  { category: "FEATURE", title: "What to Wear in the Panopticon", color: "#22201a", image: "/images/articles/panopticon/marcocover.png", href: "/articles/panopticon" },
  { category: "COLLAB", title: "Why Virtual Try-On May Finally Be Ready for the Mainstream", color: "#22201a", image: "/images/articles/trystyle1.webp", href: "/articles/TryStyle" },
  { category: "ARTICLE", title: "Biomaterials 2.0: The New Logic of Next-Generation Fashion Materials", color: "#1a1f22", image: "/images/articles/BiomaterialsBag3.jpg", href: "/articles/Biomaterials26" },
  { category: "PODCAST", title: "Can AI Turn Brands Into Better Storytellers?", color: "#221a1f", image: "/images/podcasts/Graswalds2.png", href: "/podcast" },
];

const SOCIALS = [
  {icon:<Ss/>,label:"Substack",url:"https://materialtalk.substack.com"},
  { icon: <InstagramIcon />, label: "Instagram", url: "https://instagram.com/materialtalk" },
  { icon: <LinkedInIcon />, label: "LinkedIn", url: "https://www.linkedin.com/company/materialtalk/" },
  { icon: <TikTokIcon />, label: "TikTok", url: "https://www.tiktok.com/@emmafeldner" },
  { icon: <YouTubeIcon />, label: "YouTube", url: "https://youtube.com/@materialtalk" },
  { icon: <SpotifyIcon />, label: "Spotify", url: "https://open.spotify.com/show/04fMVuTgkMzrD6cNDZViiN" },
];

export default function MaterialTalk() {
  const [email, setEmail] = useState("");
  // const [searchOpen, setSearchOpen] = useState(false);
  // const [searchQuery, setSearchQuery] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const [hoveredNav, setHoveredNav] = useState(null);
  const [hoveredSide, setHoveredSide] = useState(null);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);   

  return (
    <div style={{
      minHeight: "100vh",
      background: "#0a0a0a",
      color: "#f5f0eb",
      fontFamily: "'DM Sans', sans-serif",
      overflow: "hidden",
    }}>
      <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;0,700;1,300;1,400&family=DM+Sans:wght@300;400;500&display=swap" rel="stylesheet" />

      {/* ===== HEADER ===== */}
      <header style={{
        padding: isMobile ? "20px 20px 0" : "20px 40px 0",
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        justifyContent: "space-between",
        alignItems: "center",
        borderBottom: "1px solid rgba(199,125,255,0.15)",
        paddingBottom: 16,
      }}>
        {/* Left: VAE Animation — fixed width so title stays centered */}
        <div style={{ width: isMobile ? "auto" : 200, display: isMobile ? "none" : "flex", alignItems: "flex-start", paddingTop: 4 }}>
          <VAEAnimation />
        </div>

        {/* Center: Logo + Nav */}
        <div style={{ textAlign: "center" }}>
          <div style={{
            fontSize: 10,
            letterSpacing: "0.4em",
            color: "#c9a96e",
            marginBottom: 4,
            fontWeight: 300,
          }}>
            EST. 2025
          </div>
          <h1 style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: isMobile ? 32 : 52,
            fontWeight: 300,
            letterSpacing: "0.12em",
            margin: 0,
            lineHeight: 1,
      background: "linear-gradient(180deg, #f5f0eb 0%, #c9a96e 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
          }}>
            MATERIAL TALK
          </h1>
          <div style={{
            fontSize: 9,
            letterSpacing: "0.35em",
            color: "rgba(201,169,110,0.6)",
            marginTop: 6,
            marginBottom: 16,
          }}>
            FASHION TECH MARKET INSIGHTS
          </div>
          <nav style={{
            display: "flex",
            gap: isMobile ? 12 : 28,
            flexWrap: "wrap",
            justifyContent: "center",
            borderTop: "1px solid rgba(201,169,110,0.1)",
            paddingTop: 12,
          }}>
            {NAV_ITEMS.map((item, i) => (
              <Link
                key={item}
                to={item === "Home" ? "/" : `/${item.toLowerCase()}`}
                onMouseEnter={() => setHoveredNav(i)}
                onMouseLeave={() => setHoveredNav(null)}
                style={{
                  color: hoveredNav === i ? "#f5f0eb" : "rgba(245,240,235,0.7)",
                  textDecoration: "none",
                  fontSize: 11,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  fontWeight: 400,
                  transition: "color 0.3s ease",
                  cursor: "pointer",
                }}
              >
                {item}
              </Link>
            ))}
          </nav>
        </div>

        {/* Right: Socials + Search */}
        <div style={{ width: isMobile ? "auto" : 200, display: "flex", justifyContent: "flex-end", paddingTop: 8 }}>
          <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
            {[
              {icon:<Ss/>,label:"Substack",url:"https://materialtalk.substack.com"},
              { icon: <InstagramIcon />, label: "Instagram", url: "https://instagram.com/materialtalk" },
              { icon: <LinkedInIcon />, label: "LinkedIn", url: "https://www.linkedin.com/company/materialtalk/" },
              { icon: <TikTokIcon />, label: "TikTok", url: "https://www.tiktok.com/@emmafeldner?_r=1&_t=ZG-95JouNbMETZ" },
              { icon: <YouTubeIcon />, label: "YouTube", url: "https://youtube.com/@materialtalk" },
              { icon: <SpotifyIcon />, label: "Spotify", url: "https://open.spotify.com/show/04fMVuTgkMzrD6cNDZViiN" },
            ].map(({ icon, label, url }) => (
              <a
                key={label}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                title={label}
                style={{
                  color: "rgba(245,240,235,0.5)",
                  transition: "color 0.3s",
                  display: isMobile ? "none" : "flex",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = "#f5f0eb")}
                onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(245,240,235,0.5)")}
              >
                {icon}
              </a>
            ))}
          </div>
          {/* <div style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            background: "rgba(245,240,235,0.05)",
            border: "1px solid rgba(201,169,110,0.15)",
            borderRadius: 2,
            padding: "6px 12px",
            transition: "border-color 0.3s",
            borderColor: searchOpen ? "rgba(201,169,110,0.4)" : "rgba(201,169,110,0.15)",
          }}>
            <SearchIcon />
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setSearchOpen(true)}
              onBlur={() => setSearchOpen(false)}
              style={{
                background: "none",
                border: "none",
                outline: "none",
                color: "#f5f0eb",
                fontSize: 12,
                fontFamily: "'DM Sans', sans-serif",
                width: searchOpen ? 160 : 80,
                transition: "width 0.3s ease",
                letterSpacing: "0.05em",
              }}
            />
          </div> */}
        </div>
      </header>

      {/* ===== MAIN CONTENT ===== */}
      <main style={{
        padding: isMobile ? "24px 20px" : "48px 40px",
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : "3fr 2fr",
        gap: 24,
        minHeight: 520,
      }}>
{/* Hero */}
        <div
        onClick={() => HERO_ARTICLE.href && (window.location.href = HERO_ARTICLE.href)}
          style={{
            backgroundImage: `url(${HERO_ARTICLE.image})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            borderRadius: 3,
            boxShadow: "inset 0 0 0 1px #0a0a0a",
            padding: 40,
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-end",
            position: "relative",
            overflow: "hidden",
            cursor: "pointer",
            border: "none",
            minHeight: 460,
          }}
        >
          {/* Decorative lines */}
          <div style={{
            position: "absolute",
            top: 24,
            left: 24,
            width: 40,
            height: 40,
            borderLeft: "1px solid rgba(201,169,110,0.3)",
            borderTop: "1px solid rgba(201,169,110,0.3)",
            zIndex: 2,
          }} />
          <div style={{
            position: "absolute",
            bottom: 24,
            right: 24,
            width: 40,
            height: 40,
            borderRight: "1px solid rgba(201,169,110,0.3)",
            borderBottom: "1px solid rgba(201,169,110,0.3)",
            zIndex: 2,
          }} />

          {/* Dark gradient overlay */}
          <div style={{
            position: "absolute",
            top: 0, left: 0, right: 0, bottom: 0,
            background: "linear-gradient(to top, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.6) 40%, rgba(0,0,0,0.3) 70%)",
            zIndex: 1,
          }} />

          {/* All text above the gradient */}
          <div style={{ position: "relative", zIndex: 2 }}>
            <div style={{
              fontSize: 10,
              letterSpacing: "0.3em",
              color: "#c9a96e",
              marginBottom: 12,
              fontWeight: 600,
            }}>
              {HERO_ARTICLE.category}
            </div>
            <h2 style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: 38,
              fontWeight: 300,
              lineHeight: 1.2,
              margin: "0 0 14px 0",
              maxWidth: "90%",
            }}>
              {HERO_ARTICLE.title}
            </h2>
            <p style={{
              fontSize: 14,
              lineHeight: 1.6,
              color: "rgba(245,240,235,0.65)",
              maxWidth: "80%",
              margin: "0 0 20px 0",
              fontWeight: 300,
            }}>
              {HERO_ARTICLE.subtitle}
            </p>
            <div style={{
              fontSize: 11,
              color: "rgba(245,240,235,0.4)",
              letterSpacing: "0.1em",
            }}>
              By <span style={{ color: "#f5f0eb" }}>{HERO_ARTICLE.author}</span> · {HERO_ARTICLE.date}
            </div>
          </div>
        </div>

        {/* Sidebar Grid */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gridTemplateRows: "repeat(3, 1fr)",
          gap: 16,
        }}>
          {SIDE_ARTICLES.map((article, i) => (
            <div
              key={i}
              onMouseEnter={() => setHoveredSide(i)}
              onMouseLeave={() => setHoveredSide(null)}
              onClick={() => {
                  if (article.href) {
                    if (article.href.startsWith("http")) {
                      window.open(article.href, "_blank");
                    } else {
                      window.location.href = article.href;
                    }
                  }
                }}
              style={{
                backgroundColor: article.color,
                backgroundImage: article.image
                  ? `linear-gradient(
                      to top,
                      ${article.color} 0%,
                      rgba(0,0,0,0.6) 40%,
                      rgba(0,0,0,0.3) 70%
                    ),
                    url(${article.image})`
                  : "none",
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                borderRadius: 3,
                padding: 20,
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-end",
                cursor: "pointer",
                border: "none",
                boxShadow: "inset 0 0 0 1px #0a0a0a",
                outline: hoveredSide === i
                  ? "1px solid rgba(201,169,110,0.35)"
                  : "none",
                transition: "border-color 0.3s, transform 0.3s",
                transform: hoveredSide === i ? "translateY(-2px)" : "none",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <div style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: hoveredSide === i
                  ? "rgba(201,169,110,0.05)"
                  : "transparent",
                transition: "background 0.3s",
              }} />
              <div style={{
                fontSize: 9,
                letterSpacing: "0.25em",
                color: article.category === "SPONSORED" ? "#8a7a5a" : "#c9a96e",
                marginBottom: 8,
                fontWeight: 600,
                position: "relative",
              }}>
                {article.category}
              </div>
              <div style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: 16,
                fontWeight: 400,
                lineHeight: 1.35,
                position: "relative",
              }}>
                {article.title}
              </div>
            </div>
          ))}
        </div>
      </main>

      {/* ===== NEWSLETTER ===== */}
              <div style={{
          margin: isMobile ? "0 20px" : "0 40px",
          padding: isMobile ? "24px 0" : "36px 48px",
          borderTop: "1px solid rgba(201,169,110,0.15)",
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: isMobile ? "stretch" : "center",
          justifyContent: "space-between",
          gap: isMobile ? 20 : 40,
        }}>
        <div>
          <div style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 26,
            fontWeight: 300,
            letterSpacing: "0.05em",
          }}>
            Join our mailing list
          </div>
          <div style={{
            fontSize: 13,
            color: "rgba(245,240,235,0.45)",
            marginTop: 4,
            fontWeight: 300,
          }}>
            Exclusive information, private events and more. Know what matters, before everyone else does.
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: 0, flexShrink: 0, width: isMobile ? "100%" : "auto" }}>
          {subscribed ? (
            <div style={{
              color: "#c9a96e",
              fontSize: 13,
              letterSpacing: "0.15em",
              padding: "12px 24px",
            }}>
              ✓ You're on the list
            </div>
          ) : (
            <>
              <input
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={{
                  background: "rgba(245,240,235,0.04)",
                  border: "1px solid rgba(201,169,110,0.2)",
                  borderRight: "none",
                  borderRadius: "2px 0 0 2px",
                  padding: "12px 20px",
                  color: "#f5f0eb",
                  fontSize: 13,
                  fontFamily: "'DM Sans', sans-serif",
                  outline: "none",
                  width: isMobile ? "100%" : 260,
                  borderRadius: isMobile ? "2px 2px 0 0" : "2px 0 0 2px",
                  borderRight: isMobile ? "1px solid rgba(201,169,110,0.2)" : "none",
                  letterSpacing: "0.05em",
                }}
              />
              <button
                onClick={async () => {
                  if (!email.includes("@")) return;
                  try {
                    await fetch("https://api.materialtalk.co/api/subscribe", {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({ email }),
                    });
                    setSubscribed(true);
                  } catch (err) {
                    console.error(err);
                  }
                }}
                style={{
                  background: "#c9a96e",
                  color: "#0a0a0a",
                  border: "none",
                  borderRadius: isMobile ? "0 0 2px 2px" : "0 2px 2px 0",
                  padding: "12px 28px",
                  fontSize: 11,
                  letterSpacing: "0.2em",
                  textTransform: "uppercase",
                  fontWeight: 500,
                  cursor: "pointer",
                  fontFamily: "'DM Sans', sans-serif",
                  transition: "background 0.3s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#d4b87a")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "#f5f0eb")}
              >
                Subscribe
              </button>
            </>
          )}
        </div>
      </div>

      {/* ===== FOOTER ===== */}
<footer style={{
  padding: isMobile ? "24px 20px" : "24px 40px",
  borderTop: "1px solid rgba(199,125,255,0.08)",
  display: "flex",
  flexDirection: isMobile ? "column" : "row",
  justifyContent: "space-between",
  alignItems: "center",
  gap: isMobile ? 16 : 0,
}}>
  <div style={{ fontSize: 10, color: "rgba(245,240,235,0.25)", letterSpacing: "0.15em" }}>
    © 2026 MATERIAL TALK. ALL RIGHTS RESERVED.
  </div>
  {isMobile && (
    <div style={{ display: "flex", gap: 16 }}>
      {SOCIALS.map(({icon, label, url}) => (
        <a key={label} href={url} title={label} target="_blank" rel="noopener noreferrer"
          style={{ color: "rgba(245,240,235,0.4)", display: "flex" }}>
          {icon}
        </a>
      ))}
    </div>
  )}
</footer>
    </div>
  );
}